<script setup>
import CountryDownloadBox from '../CountryDownloadBox/CountryDownloadBox.vue';
import { CountryDownloadBoxContext } from '../../globals/constants/download';
import DownloadButton from '../DownloadButton/DownloadButton.vue';
import { mergeSwitchWtParams } from '../../utils/tracking';
import { onMounted, ref } from 'vue';
import Icon from '../Icon/Icon.vue';
import Divider from '../Divider/Divider.vue';

const props = defineProps({
  result: {
    type: Object,
    default: () => {},
  },
  labels: {
    type: Object,
    default: () => {},
  },
  wtParams: {
    type: Object,
    default: () => {},
  },
  hideTooltip: {
    type: Boolean,
    default: false,
  },
});
function getLocalizedUrlAndTitle(urls, fallbackLang = 'en') {
  if (!Array.isArray(urls) || urls.length === 0) return null;
  let pageLang = 'en';
  try {
    pageLang = document.documentElement.lang?.split('-')[0] || pageLang;
    const match = window.location.pathname.match(/\/([a-z]{2})(?:-[a-z]{2})?\//i);
    if (match && match[1]) pageLang = match[1];
  } catch {
    // ignore
  }
  pageLang = pageLang.toLowerCase();
  fallbackLang = fallbackLang.toLowerCase();
  let found = urls.find((u) => u.language && u.language.toLowerCase() === pageLang);
  if (!found) found = urls.find((u) => u.language && u.language.toLowerCase() === fallbackLang);
  if (!found) found = urls[0];
  return found;
}

const localizedUrlTitle = props.result.urls ? getLocalizedUrlAndTitle(props.result.urls) : null;

const hasMoreSubHeading = ref(false);
const expandedSubHeading = ref(false);
const section = ref('');
const expandableSubHeading = ref(null);

const mergeWtParams = (globalWtParams, itemWtParams, itemParams) => {
  return mergeSwitchWtParams(globalWtParams, itemWtParams, itemParams);
};

const maxScrollHeight = 25;

const checkExpandable = () => {
  hasMoreSubHeading.value =
    expandableSubHeading.value && expandableSubHeading.value.scrollHeight > maxScrollHeight;
};

const toggleExpandableSubHeading = () => {
  expandableSubHeading.value.style.height = expandedSubHeading.value ? '' : 'auto';
  expandedSubHeading.value = !expandedSubHeading.value;
};

const getContext = () => {
  return CountryDownloadBoxContext.DOWNLOAD_LIST_TEASER;
};

const createConfigFromSoftwareResult = (downloadable, productTypes, description, isManual) => {
  return {
    labels: {
      wishlist: downloadable.text,
    },
    metadata: {
      fileType: '',
      fileSize: '',
    },
    title: downloadable.text,
    wishlistTitle: isManual ? `${downloadable.text}: ${description}` : description,
    types: productTypes || [],
    urls: {
      file: '',
      language: '',
      url: downloadable.url,
    },
    hideTooltip: true,
  };
};

const createConfigFrom3dDataResult = (result) => {
  return {
    labels: {
      wishlist: result.subHeading,
    },
    metadata: {
      fileType: '',
      fileSize: '',
    },
    text: result.text,
    title: result.fileInfo,
    wishlistTitle: result.description,
    types: result.productTypes || [],
    urls: {
      file: '',
      language: '',
      url: result.link,
    },
    hideTooltip: true,
  };
};

onMounted(() => {
  checkExpandable();

  if (window.downloadCategory) {
    section.value = window.downloadCategory;
  }
});
</script>

<template>
  <div :class="$style.container">
    <div :class="$style.content">
      <div :class="$style.upper">
        <div>
          <h5 v-if="localizedUrlTitle" :class="$style.title" v-html="localizedUrlTitle.title" />
          <h5 v-else-if="result.title" :class="$style.title" v-html="result.title" />
          <div v-if="result.description" :class="$style.description" v-html="result.description" />
        </div>
        <img v-if="result.image" alt="" :class="$style.image" :src="result.image" />
      </div>

      <dl v-if="result.text" :class="[$style.table, $style.descriptionList]">
        <template v-for="(elm, key) in result.text" :key="key">
          <template v-if="Array.isArray(elm)">
            <dt :class="[$style.descriptionListTitle]">{{ key }}:</dt>
            <dd :class="[$style.descriptionListDescription]">
              <span :class="$style.text"> {{ elm.join(', ') }} </span>
            </dd>
          </template>
          <template v-else>
            <dt :class="[$style.descriptionListTitle]">{{ key }}:</dt>
            <dd :class="[$style.descriptionListDescription]">
              <span :class="$style.text">{{ elm }}</span>
            </dd>
          </template>
        </template>
      </dl>

      <CountryDownloadBox
        v-if="result.files"
        :class="$style.downloadBox"
        :context="getContext()"
        :countries="result.files"
        :hide-tooltip="hideTooltip"
        :labels="labels"
        :section="section"
        :title="result.title || result.subHeading || result.description"
        :types="result.productTypes"
        :wt-params="mergeWtParams(wtParams, result.wtParams, result)"
      />

      <div v-if="result.software" :class="$style.software">
        <DownloadButton
          v-if="result.software.file"
          :config="
            createConfigFromSoftwareResult(
              result.software.file,
              result.productTypes,
              result.description,
              false,
            )
          "
          :labels="labels"
          :section="section"
          :wt-params="{
            ...wtParams,
            ...result.software.file.wtParams,
          }"
        />
        <DownloadButton
          v-if="result.software.manual"
          colour="white"
          :config="
            createConfigFromSoftwareResult(
              result.software.manual,
              result.productTypes,
              result.description,
              true,
            )
          "
          :labels="labels"
          :section="section"
        />
      </div>

      <div v-if="result.link" :class="$style.data3d">
        <DownloadButton
          :config="createConfigFrom3dDataResult(result)"
          :labels="labels"
          :section="section"
          :wt-params="mergeWtParams(wtParams, result.wtParams, result)"
        />
      </div>

      <Divider v-if="result.subHeading" :class="$style.divider" />

      <div
        v-if="result.subHeading"
        ref="expandableSubHeading"
        :class="$style.subHeading"
        @click="toggleExpandableSubHeading"
      >
        <div
          :class="[
            hasMoreSubHeading && $style.expandPane,
            hasMoreSubHeading && expandedSubHeading && $style.isExpanded,
          ]"
        >
          <template
            v-if="
              Array.isArray(result.subHeading) ||
              Array.isArray(result.subHeading[Object.keys(result.subHeading).shift()])
            "
          >
            <span v-for="(elm, key) in result.subHeading" :key="key">
              {{ key }}
              <template v-if="Array.isArray(elm)">
                <template v-for="(type, index) in elm">
                  <template v-if="index !== 0">, </template>
                  {{ type }}
                </template>
              </template>
              <template v-else>{{ elm }}</template>
            </span>
          </template>
          <template v-else>
            <span>{{ result.subHeading }}</span>
          </template>
        </div>
        <Icon
          v-if="hasMoreSubHeading"
          :class="$style.expandToggle"
          :name="expandedSubHeading ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
        />
      </div>
    </div>
  </div>
</template>

<style module lang="scss">
@forward './DownloadListTeaser.scss';
</style>
